import React, { useState, useRef, useEffect, useMemo } from 'react';
import {
  Fab,
  Drawer,
  Box,
  Typography,
  TextField,
  IconButton,
  CircularProgress,
  Avatar,
  Tooltip,
  LinearProgress,
  Collapse,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import SmartToyOutlinedIcon from '@mui/icons-material/SmartToyOutlined';
import CloseIcon from '@mui/icons-material/Close';
import SendIcon from '@mui/icons-material/Send';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ChatIcon from '@mui/icons-material/Chat';
import SchoolIcon from '@mui/icons-material/School';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import ReactMarkdown from 'react-markdown';
import { config } from '../config/env';

interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
}

interface AssistantHttpError extends Error {
  status?: number;
  retryAfter?: number;
}

interface AiAssistantContext {
  currentMainTab: number;
  currentCvTab: number;
  currentTabIndex: number;
  hasProfilePhoto: boolean;
  workExperienceCount: number;
  educationCount: number;
  hasJobDescription: boolean;
  hasMotivationLetter: boolean;
  matchScore: number | null;
  hasData: boolean;
  hasApplications: boolean;
  cvDataSetCount: number;
  applicationCount: number;
  hasOptimizedCv: boolean;
  cvTemplateSettingsSaved: boolean;
  cvPreviewPageCount?: number;
  language: string;
  activeModal: string | null;
  userId: string | null;
  agbAccepted: boolean;
}

interface AiAssistantProps {
  context: AiAssistantContext;
  onTutorialReset?: () => void;
}

type ActiveTab = 'chat' | 'tutorial';

interface TutorialStep {
  id: string;
  optional?: boolean;
  getTitle: (lang: string) => string;
  getInstruction: (lang: string, ctx?: AiAssistantContext) => string;
  isCompleted: (ctx: AiAssistantContext, modalFlags: ModalCloseFlags) => boolean;
}

interface ModalCloseFlags {
  cvDataUploadedAndModalClosed: boolean;
  cvDataModalClosed: boolean;
  cvPreviewModalClosedAfterOpt: boolean;
  cvTemplateSettingsSaved: boolean;
  motivationModalClosed: boolean;
  appDetailsModalClosed: boolean;
  jobDescriptionCompleted: boolean;
  matchScoreCompleted: boolean;
  cvOptimizedCompleted: boolean;
}

const buildTutorialSteps = (): TutorialStep[] => [
  {
    id: 'uploadCvData',
    getTitle: (lang) => {
      switch (lang) {
        case 'en': return 'Upload CV data';
        case 'fr': return 'Télécharger les données CV';
        case 'it': return 'Caricare i dati CV';
        default: return 'CV-Daten hochladen';
      }
    },
    getInstruction: (lang) => {
      switch (lang) {
        case 'en': return 'Go to the **"CV Data"** tab and click the **+ icon** on the right side of the **"My CV Data"** table header. You can enter your data manually or upload an existing CV as a **PDF** or **Word file**. The AI will automatically extract all relevant information from your document.';
        case 'fr': return 'Va dans l\'onglet **«Données CV»** et clique sur l\'**icône +** à droite dans l\'en-tête du tableau **«Mes données CV»**. Tu peux saisir tes données manuellement ou télécharger un CV existant en **PDF** ou **fichier Word**. L\'IA extraira automatiquement toutes les informations pertinentes de ton document.';
        case 'it': return 'Vai alla scheda **"Dati CV"** e clicca sull\'**icona +** a destra nell\'intestazione della tabella **"I miei dati CV"**. Puoi inserire i dati manualmente o caricare un CV esistente come **PDF** o **file Word**. L\'IA estrarrà automaticamente tutte le informazioni rilevanti dal tuo documento.';
        default: return 'Gehe zum Tab **„CV Daten"** und klicke rechts im Header der Tabelle **"Meine CV Daten"** auf das **+ Icon**. Du kannst deine Daten manuell eingeben oder ein bestehendes CV als **PDF-** oder **Word-Datei** hochladen. Die KI extrahiert automatisch alle relevanten Informationen aus deinem Dokument.';
      }
    },
    isCompleted: (ctx) => ctx.cvDataSetCount > 0,
  },
  {
    id: 'reviewCvData',
    getTitle: (lang) => {
      switch (lang) {
        case 'en': return 'Review CV data';
        case 'fr': return 'Vérifier les données CV';
        case 'it': return 'Controllare i dati CV';
        default: return 'CV-Daten prüfen';
      }
    },
    getInstruction: (lang) => {
      switch (lang) {
        case 'en': return 'Now review your CV data and check whether entries such as **work experience**, **education**, **skills**, and other sections are complete and correct. If you made changes, save the data. When you are done, close the **CV data modal**.';
        case 'fr': return 'Vérifie maintenant tes données CV et contrôle que les éléments comme l\'**expérience professionnelle**, la **formation**, les **compétences** et les autres sections sont complets et corrects. Si tu as fait des modifications, enregistre les données. Quand tu as terminé, ferme la **fenêtre des données CV**.';
        case 'it': return 'Ora controlla i tuoi dati CV e verifica che voci come **esperienza lavorativa**, **formazione**, **competenze** e altre sezioni siano complete e corrette. Se hai apportato modifiche, salva i dati. Quando hai finito, chiudi la **finestra dei dati CV**.';
        default: return 'Kontrolliere nun in den CV-Daten, ob Einträge wie **Berufserfahrung**, **Ausbildung**, **Fähigkeiten** und weitere Bereiche vollständig und korrekt sind. Hast du änderungen vorgenommen, speichere die Daten. Wenn du fertig bist, schliesse das **CV-Daten-Modal**.';
      }
    },
    isCompleted: (ctx, flags) => (flags.cvDataUploadedAndModalClosed && ctx.cvDataSetCount > 0) || ctx.hasJobDescription || flags.jobDescriptionCompleted,
  },
  {
    id: 'uploadJobDescription',
    getTitle: (lang) => {
      switch (lang) {
        case 'en': return 'Upload job description';
        case 'fr': return 'Télécharger la description du poste';
        case 'it': return 'Caricare la descrizione del lavoro';
        default: return 'Stellenbeschreibung hochladen';
      }
    },
    getInstruction: (lang) => {
      switch (lang) {
        case 'en': return 'Go to the **"Applications"** tab and click the **+ icon** on the right side of the **"My Applications"** table header. Copy the **text** of the job description and paste it into the **dashed-bordered area**. Alternatively, you can drag the **URL** of the job description onto this **area**. The AI will automatically identify the relevant requirements.';
        case 'fr': return 'Va dans l\'onglet **«Candidatures»** et clique sur l\'**icône +** à droite dans l\'en-tête du tableau **«Mes candidatures»**. Copie le **texte** de la description du poste et colle-le dans la **zone encadrée en pointillés**. Tu peux aussi glisser l\'**URL** de la description du poste sur cette **zone**. L\'IA reconnaîtra automatiquement les exigences pertinentes.';
        case 'it': return 'Vai alla scheda **"Candidature"** e clicca sull\'**icona +** a destra nell\'intestazione della tabella **"Le mie candidature"**. Copia il **testo** della descrizione del lavoro e incollalo nell\'**area con bordo tratteggiato**. In alternativa, puoi trascinare l\'**URL** della descrizione del lavoro su quest\'**area**. L\'IA riconoscerà automaticamente i requisiti rilevanti.';
        default: return 'Gehe zum Tab **Bewerbungen** und klicke rechts im Header der Tabelle **"Meine Bewerbungen"** auf das **+ Icon**. Kopiere den **Text** der Stellenbeschreibung und füge ihn in die **gestrichelt umrandete Fläche** ein. Alternativ kannst du auch die **URL** der Stellenbeschreibung auf diese **Fläche** ziehen. Die KI erkennt automatisch die relevanten Anforderungen.';
      }
    },
    isCompleted: (ctx, flags) => ctx.hasJobDescription || flags.jobDescriptionCompleted,
  },
  {
    id: 'calculateMatch',
    getTitle: (lang) => {
      switch (lang) {
        case 'en': return 'Calculate match score';
        case 'fr': return 'Calculer la compatibilité';
        case 'it': return 'Calcolare la compatibilità';
        default: return 'Passgenauigkeit berechnen';
      }
    },
    getInstruction: (lang) => {
      switch (lang) {
        case 'en': return 'After entering your CV data and job description, click the first purple button **"Check Match Score"**. The AI compares your profile with the requirements and shows you a matching score as a percentage.';
        case 'fr': return 'Après avoir saisi tes données CV et la description du poste, clique sur le premier bouton violet **«Vérifier la correspondance»**. L\'IA compare ton profil avec les exigences et t\'affiche un score de correspondance en pourcentage.';
        case 'it': return 'Dopo aver inserito i dati CV e la descrizione del lavoro, clicca sul primo pulsante viola **"Verifica corrispondenza"**. L\'IA confronta il tuo profilo con i requisiti e ti mostra un punteggio di corrispondenza in percentuale.';
        default: return 'Nachdem du deine CV-Daten und die Stellenbeschreibung eingegeben hast, klicke auf den ersten violetten Button **„Passgenauigkeit prüfen"**. Die KI vergleicht dein Profil mit den Anforderungen und zeigt dir einen Matching-Score in Prozent an.';
      }
    },
    isCompleted: (ctx, flags) => ctx.matchScore !== null || flags.matchScoreCompleted,
  },
  {
    id: 'adjustCvData',
    getTitle: (lang) => {
      switch (lang) {
        case 'en': return 'Adjust CV data';
        case 'fr': return 'Ajuster les données CV';
        case 'it': return 'Adattare i dati CV';
        default: return 'CV-Daten anpassen';
      }
    },
    getInstruction: (lang, ctx) => {
      const score = ctx?.matchScore !== null && ctx?.matchScore !== undefined ? `**${ctx.matchScore}%**` : '—';
      const below75 = ctx?.matchScore !== null && ctx?.matchScore !== undefined && ctx.matchScore < 75;
      switch (lang) {
        case 'en': return `In the new window you can see the current match score (${score}).${below75 ? ' The score should be at least **75%**.' : ''} \n\r Below the match score you\'ll find a list of **missing skills, missing capabilities and weaknesses**. **Add skills and experiences** truthfully to your **CV data**. You can add information in the tabs **"Work Experience"**, **"Education"** or **"Additional Information"** on the right. Click the **"Recalculate match score" icon** in the upper left corner to calculate new values. When you\'re satisfied with the result, click the **"Accept match score" icon** to the upper right of the match score.`;
        case 'fr': return `Dans la nouvelle fenêtre, tu peux voir le score de correspondance actuel (${score}).${below75 ? ' Le score devrait être d\'au moins **75%**.' : ''} \n\r En dessous du score, tu trouveras une liste des **skills manquants, aptitudes manquantes et faiblesses**. **Ajoute des compétences et expériences** de manière véridique dans tes **données CV**. Tu peux compléter les informations dans les onglets **«Expérience professionnelle»**, **«Formation»** ou **«Informations supplémentaires»** à droite. Clique sur l\'**icône «Recalculer la correspondance»** dans le coin supérieur gauche pour recalculer les valeurs. Quand tu es satisfait du résultat, clique sur l\'**icône «Accepter le score de correspondance»** en haut à droite du score.`;
        case 'it': return `Nella nuova finestra puoi vedere il punteggio di corrispondenza attuale (${score}).${below75 ? ' Il punteggio dovrebbe essere almeno del **75%**.' : ''} \n\r Sotto il punteggio troverai un elenco di **skill mancanti, capacità mancanti e punti deboli**. **Aggiungi competenze ed esperienze** in modo veritiero nei tuoi **dati CV**. Puoi aggiungere informazioni nelle schede **"Esperienza lavorativa"**, **"Formazione"** o **"Informazioni aggiuntive"** a destra. Clicca sull\'**icona "Ricalcola corrispondenza"** nell\'angolo in alto a sinistra per calcolare nuovi valori. Quando sei soddisfatto del risultato, clicca sull\'**icona "Accetta il punteggio di corrispondenza"** in alto a destra del punteggio.`;
        default: return `Im neuen Fenster kannst du die aktuelle Passgenauigkeit sehen (${score}).${below75 ? ' Dieser Wert sollte mindestens **75%** betragen.' : ''} \n\r Unterhalb der Passgenauigkeit findest du eine Liste mit **fehlenden Skills, fehlenden Fähigkeiten und Schwächen**. **Ergänze Fähigkeiten und Erfahrungen** wahrheitsgetreu in deinen **CV-Daten**. Hierzu kannst du rechts in den Tabs **"Berufserfahrung"**, **"Ausbildung"** oder **"Weitere Angaben"** Informationen ergänzen. Mit Klick auf das **Icon "Passgenauigkeit neu berechnen"** in der oberen linken Ecke kannst du neue Werte neu berechnen lassen. Bist du mit dem Ergebnis zufrieden, klicke auf das **Icon "Passgenauigkeit akzeptieren"** oben rechts von der Passgenauigkeit.`;
      }
    },
    isCompleted: (_ctx, flags) => flags.cvDataModalClosed,
  },
  {
    id: 'optimizeCv',
    getTitle: (lang) => {
      switch (lang) {
        case 'en': return 'Optimize CV';
        case 'fr': return 'Optimiser le CV';
        case 'it': return 'Ottimizzare il CV';
        default: return 'CV optimieren';
      }
    },
    getInstruction: (lang) => {
      switch (lang) {
        case 'en': return 'Now click the second purple button **"Optimize CV"** to start the **CV optimization**. The AI will align your CV data with the job description and create an optimized CV for you.';
        case 'fr': return 'Clique maintenant sur le deuxième bouton violet **«Optimiser le CV»** pour lancer l\'**optimisation du CV**. L\'IA alignera tes données CV sur la description du poste et créera un CV optimisé pour toi.';
        case 'it': return 'Ora clicca sul secondo pulsante viola **"Ottimizza CV"** per avviare l\'**ottimizzazione del CV**. L\'IA allineerà i tuoi dati CV alla descrizione del lavoro e creerà un CV ottimizzato per te.';
        default: return 'Klicke nun auf den zweiten violetten Button **„CV optimieren"**, um die **CV-Optimierung anzustossen**. Die KI richtet deine CV-Daten auf die Stellenbeschreibung aus und erstellt für dich einen optimierten CV.';
      }
    },
    isCompleted: (ctx, flags) => ctx.hasOptimizedCv || flags.cvOptimizedCompleted,
  },
  {
    id: 'adjustTemplateSettings',
    getTitle: (lang) => {
      switch (lang) {
        case 'en': return 'Customize CV template';
        case 'fr': return 'Personnaliser le modèle CV';
        case 'it': return 'Personalizzare il modello CV';
        default: return 'CV-Template anpassen';
      }
    },
    getInstruction: (lang) => {
      switch (lang) {
        case 'en': return 'Choose one of the templates **Modern**, **Professional**, or **Classic** and customize the design using the **settings on the left side** (for example show or hide information, insert graphics, adjust colors, layout, and formatting options). When your design is ready, click the **Save icon** in the **upper settings area**.';
        case 'fr': return 'Choisis l\'un des modèles **Modern**, **Professional** ou **Classic**, puis adapte le design avec les **paramètres sur le côté gauche** (par exemple afficher ou masquer des informations, insérer des graphiques, ajuster les couleurs, la mise en page et les options de formatage). Quand ton design est prêt, clique sur l\'**icône Enregistrer** dans la **zone supérieure des paramètres**.';
        case 'it': return 'Scegli uno dei modelli **Modern**, **Professional** o **Classic** e personalizza il design con le **impostazioni sul lato sinistro** (ad esempio mostrare o nascondere informazioni, inserire grafici, regolare colori, layout e opzioni di formattazione). Quando il design è pronto, clicca sull\'**icona Salva** nella **parte superiore dell\'area impostazioni**.';
        default: return 'Wähle eines der Templates Modern, Professional oder Classic und passe das Design mit den **Einstellungen auf der linken Seite** an (z. B. Ein- und Ausblenden von Informationen, Einfügen von Grafiken, anpassen von Farben, Layout und Formatierungsoptionen). Wenn dein Design bereit ist, klicke im oben Einstellungsbereich auf das **Save-Icon**.';
      }
    },
    isCompleted: (ctx, flags) => ctx.cvTemplateSettingsSaved || flags.cvTemplateSettingsSaved || flags.cvPreviewModalClosedAfterOpt,
  },
  {
    id: 'adjustOptimizedCv',
    getTitle: (lang) => {
      switch (lang) {
        case 'en': return 'Adjust CV';
        case 'fr': return 'Ajuster le CV';
        case 'it': return 'Adattare il CV';
        default: return 'CV anpassen';
      }
    },
    getInstruction: (lang) => {
      switch (lang) {
        case 'en': return 'Now **review the texts and lists** in the **CV preview**. You can edit individual sections directly in the preview - just click on the areas you want to adjust. Hide individual work experiences by marking them as **"Not relevant"**, or refine the texts until you are satisfied. You can **download** your **CV** using the **Download icon** in the header.';
        case 'fr': return 'Ensuite, **vérifie les textes et les listes** dans l\'**aperçu CV**. Tu peux modifier des sections directement dans l\'aperçu - clique simplement sur les zones que tu souhaites ajuster. Masque certaines expériences professionnelles en les marquant comme **"Non pertinentes"**, ou affine les textes jusqu\'à ce que tu sois satisfait. Tu peux **télécharger** ton **CV** avec l\'**icône Télécharger** dans l\'en-tête.';
        case 'it': return 'Ora **controlla i testi e le liste** nell\'**anteprima CV**. Puoi modificare le singole sezioni direttamente nell\'anteprima - basta cliccare sulle aree che vuoi modificare. Nascondi singole esperienze lavorative contrassegnandole come **"Non rilevanti"**, oppure perfeziona i testi finché non sei soddisfatto. Puoi **scaricare** il tuo **CV** con l\'**icona Download** nell\'intestazione.';
        default: return 'Überprüfe nun die **Texte und Listen** in der **CV-Vorschau**. Du kannst einzelne Abschnitte direkt in der Vorschau bearbeiten – klicke einfach auf die Bereiche, die du anpassen möchtest. Blende einzelne Berufserfahrungen aus, in dem du sie als "Nicht relevant" markierts oder feile an den Texten, bis du zufrieden bist. Du kannst dir deinen **CV** mit dem **Download-Icon** oben im Header **herunterladen**.';
      }
    },
    isCompleted: (_ctx, flags) => flags.cvPreviewModalClosedAfterOpt,
  },
  {
    id: 'motivationLetter',
    optional: true,
    getTitle: (lang) => {
      switch (lang) {
        case 'en': return 'Create motivation letter';
        case 'fr': return 'Créer la lettre de motivation';
        case 'it': return 'Creare la lettera di motivazione';
        default: return 'Motivationsschreiben erstellen';
      }
    },
    getInstruction: (lang) => {
      switch (lang) {
        case 'en': return 'Create a matching **cover letter** with the help of AI. Click the third purple button **"Create Cover Letter"**. In the new window, you can optionally enter text about your motivation. Click **"Create Cover Letter"** to have the AI automatically generate a text tailored to the position.';
        case 'fr': return 'Crée une **lettre de motivation** adaptée grâce à l\'IA. Clique sur le troisième bouton violet **«Créer une lettre de motivation»**. Dans la nouvelle fenêtre, tu peux optionnellement saisir des textes sur ta motivation. En cliquant sur **«Créer une lettre de motivation»**, l\'IA créera automatiquement un texte adapté au poste.';
        case 'it': return 'Crea una **lettera di motivazione** adatta con l\'aiuto dell\'IA. Clicca sul terzo pulsante viola **"Crea lettera di motivazione"**. Nella nuova finestra puoi opzionalmente inserire testi sulla tua motivazione. Cliccando su **"Crea lettera di motivazione"**, l\'IA creerà automaticamente un testo su misura per la posizione.';
        default: return 'Erstelle ein passendes **Motivationsschreiben** mit Hilfe der KI. Klicke auf den dritten violetten Button **„Motivationsschreiben erstellen"**. Im neuen Fenster kannst du optional Texte zu deiner Motivation angeben. Mit Klick auf **"Motivationsschreiben erstellen"** erstellt die KI automatisch einen auf die Stelle zugeschnittenen Text.';
      }
    },
    isCompleted: (_ctx, flags) => flags.motivationModalClosed || flags.appDetailsModalClosed,
  },
  {
    id: 'viewApplication',
    getTitle: (lang) => {
      switch (lang) {
        case 'en': return 'View application';
        case 'fr': return 'Voir la candidature';
        case 'it': return 'Visualizza candidatura';
        default: return 'Bewerbung ansehen';
      }
    },
    getInstruction: (lang) => {
      switch (lang) {
        case 'en': return 'Click the button **"Back to my applications"** at the bottom. With the creation of the optimized CV, an **application** was also created. Click on the **application** to see the **details**. In the new window you can see details about the **job description**, **add comments** and **manage the status**. You can also open the optimized **CV** here at any time.';
        case 'fr': return 'Clique en bas sur le bouton **"Retour à mes candidatures"**. Avec la création du CV optimisé, une **candidature** a également été créée. Clique sur la **candidature** pour voir les **détails**. Dans la nouvelle fenêtre, tu peux voir les détails de la **description du poste**, **saisir des commentaires** et **gérer le statut**. Tu peux aussi ouvrir le **CV** optimisé ici à tout moment.';
        case 'it': return 'Clicca in basso sul pulsante **"Torna alle mie candidature"**. Con la creazione del CV ottimizzato, è stata creata anche una **candidatura**. Clicca sulla **candidatura** per vedere i **dettagli**. Nella nuova finestra puoi vedere i dettagli della **descrizione del lavoro**, **inserire commenti** e **gestire lo stato**. Puoi anche aprire il **CV** ottimizzato qui in qualsiasi momento.';
        default: return 'Klicke unten auf den Button **"Zurück zu meinen Bewerbungen"**. Mit der Erstellung des optimierten CVs wurde auch eine **Bewerbung** erstellt. Klicke auf die **Bewerbung**, um die **Details** zu sehen. Im neuen Fenster kannst du Details zur **Stellenbeschreibung sehen**, **Kommentare erfassen** und den **Status pflegen**. Weiter kannst du hier jederzeit den optimierten **CV** öffnen.';
      }
    },
    isCompleted: (_ctx, flags) => flags.appDetailsModalClosed,
  },
];

export default function AiAssistant({ context, onTutorialReset }: AiAssistantProps) {
  const COLLAPSED_DRAWER_WIDTH = 56;
  const EXPANDED_DRAWER_WIDTH = 400;
  const COLLAPSE_BREAKPOINT = 1600;
  const COLLAPSE_TRANSITION_MS = 680;
  // Ensure assistant is above dialogs and backdrops.
  const ASSISTANT_Z_INDEX = 3000;
  const [open, setOpen] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [pointerExitedOnRightEdge, setPointerExitedOnRightEdge] = useState(false);
  const [hasDomModalOpen, setHasDomModalOpen] = useState(false);
  const [viewportWidth, setViewportWidth] = useState(
    typeof window !== 'undefined' ? window.innerWidth : 1920
  );
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPulse, setShowPulse] = useState(false);
  const [activeTab, setActiveTab] = useState<ActiveTab>(() => {
    if (context.userId && localStorage.getItem(`tutorialCompleted_${context.userId}`) === 'true') {
      return 'chat';
    }
    return 'tutorial';
  });
  const [expandedStep, setExpandedStep] = useState<string | null>(null);
  const [showTutorialToggle, setShowTutorialToggle] = useState(false);
  const [showTutorial, setShowTutorial] = useState(() => {
    if (context.userId && localStorage.getItem(`tutorialCompleted_${context.userId}`) === 'true') {
      return false;
    }
    return true;
  });

  // Modal-Close Tracking fuer Tutorial-Schritte 2, 5, 8, 9, 10 (persistiert in localStorage)
  const savedProgress = (() => {
    try {
      const saved = localStorage.getItem(`tutorialProgress_${context.userId}`);
      return saved ? JSON.parse(saved) : {};
    } catch { return {}; }
  })();
  const [cvDataUploadedAndModalClosed, setCvDataUploadedAndModalClosed] = useState(savedProgress.cvDataUploadedAndModalClosed || false);
  const [cvDataModalClosed, setCvDataModalClosed] = useState(savedProgress.cvDataModalClosed || false);
  const [cvPreviewModalClosedAfterOpt, setCvPreviewModalClosedAfterOpt] = useState(savedProgress.cvPreviewModalClosedAfterOpt || false);
  const [cvTemplateSettingsSaved, setCvTemplateSettingsSaved] = useState(savedProgress.cvTemplateSettingsSaved || false);
  const [motivationModalClosed, setMotivationModalClosed] = useState(savedProgress.motivationModalClosed || false);
  const [appDetailsModalClosed, setAppDetailsModalClosed] = useState(savedProgress.appDetailsModalClosed || false);
  const [jobDescriptionCompleted, setJobDescriptionCompleted] = useState(savedProgress.jobDescriptionCompleted || false);
  const [matchScoreCompleted, setMatchScoreCompleted] = useState(savedProgress.matchScoreCompleted || false);
  const [cvOptimizedCompleted, setCvOptimizedCompleted] = useState(savedProgress.cvOptimizedCompleted || false);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const tutorialScrollRef = useRef<HTMLDivElement | null>(null);
  const stepRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const inputRef = useRef<HTMLInputElement>(null);
  const autoOpenedRef = useRef(false);
  const prevStateRef = useRef({
    activeModal: null as string | null,
    hasJobDescription: false,
    matchScore: null as number | null,
    hasOptimizedCv: false,
    cvTemplateSettingsSaved: false,
    hasMotivationLetter: false,
    cvDataSetCount: 0,
    applicationCount: 0,
  });
  const initialLoadRef = useRef(true);
  const openRef = useRef(false);
  const cvLengthHintShownRef = useRef(false);

  const modalCloseFlags: ModalCloseFlags = {
    cvDataUploadedAndModalClosed,
    cvDataModalClosed,
    cvPreviewModalClosedAfterOpt,
    cvTemplateSettingsSaved,
    motivationModalClosed,
    appDetailsModalClosed,
    jobDescriptionCompleted,
    matchScoreCompleted,
    cvOptimizedCompleted,
  };

  const tutorialSteps = buildTutorialSteps();

  // Tutorial-Fortschritt in localStorage persistieren
  useEffect(() => {
    if (!context.userId) return;
    const progressData = {
      cvDataUploadedAndModalClosed,
      cvDataModalClosed,
      cvPreviewModalClosedAfterOpt,
      cvTemplateSettingsSaved,
      motivationModalClosed,
      appDetailsModalClosed,
      jobDescriptionCompleted,
      matchScoreCompleted,
      cvOptimizedCompleted,
    };
    localStorage.setItem(`tutorialProgress_${context.userId}`, JSON.stringify(progressData));
  }, [cvDataUploadedAndModalClosed, cvDataModalClosed, cvPreviewModalClosedAfterOpt, cvTemplateSettingsSaved, motivationModalClosed, appDetailsModalClosed, jobDescriptionCompleted, matchScoreCompleted, cvOptimizedCompleted, context.userId]);

  // Halte openRef synchron mit open state (fuer Zugriff in async callbacks)
  useEffect(() => {
    openRef.current = open;
  }, [open]);

  useEffect(() => {
    const handleResize = () => setViewportWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (!open || typeof document === 'undefined') {
      setHasDomModalOpen(false);
      return;
    }

    const detectModal = () => {
      const hasOpenDialog = !!document.querySelector('.MuiDialog-root[aria-hidden="false"], .MuiDialog-root:not([aria-hidden]), [role="dialog"]');
      setHasDomModalOpen(hasOpenDialog);
    };

    detectModal();
    const intervalId = window.setInterval(detectModal, 250);
    return () => window.clearInterval(intervalId);
  }, [open]);

  const hasModalOverlapRisk = useMemo(() => {
    return viewportWidth < COLLAPSE_BREAKPOINT && (!!context.activeModal || hasDomModalOpen);
  }, [viewportWidth, context.activeModal, hasDomModalOpen]);

  useEffect(() => {
    if (!open || !hasModalOverlapRisk) {
      setIsCollapsed(false);
    }
  }, [open, hasModalOverlapRisk]);

  useEffect(() => {
    if (!open) return;
    if (hasModalOverlapRisk && !isHovered && !pointerExitedOnRightEdge) {
      setIsCollapsed(true);
    }
  }, [open, hasModalOverlapRisk, isHovered, pointerExitedOnRightEdge]);

  // Tutorial: Verwende eine monotone Anzeige-Logik, damit spaetere Schritte
  // nicht als erledigt angezeigt werden koennen, wenn fruehere noch offen sind.
  const tutorialDisplayCompletionMap = useMemo(() => {
    const map: Record<string, boolean> = {};
    let allPreviousStepsCompleted = true;

    tutorialSteps.forEach((step) => {
      const rawCompleted = step.isCompleted(context, modalCloseFlags);
      const displayCompleted = allPreviousStepsCompleted && rawCompleted;
      map[step.id] = displayCompleted;
      if (!displayCompleted) {
        allPreviousStepsCompleted = false;
      }
    });

    return map;
  }, [tutorialSteps, context, modalCloseFlags]);

  const completedStepIds = useMemo(
    () => tutorialSteps
      .filter((step) => tutorialDisplayCompletionMap[step.id])
      .map((step) => step.id),
    [tutorialSteps, tutorialDisplayCompletionMap]
  );

  const firstIncompleteStepId = useMemo(
    () => tutorialSteps.find((step) => !tutorialDisplayCompletionMap[step.id])?.id ?? null,
    [tutorialSteps, tutorialDisplayCompletionMap]
  );

  // Tutorial: Berechne Fortschritt
  const completedSteps = completedStepIds.length;
  const totalSteps = tutorialSteps.length;
  const progressPercent = Math.round((completedSteps / totalSteps) * 100);

  // Tutorial: Immer den ersten nicht abgeschlossenen Schritt expandieren
  const prevCompletedRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    const currentCompleted = new Set(completedStepIds);
    const newlyCompleted = Array.from(currentCompleted).some(id => !prevCompletedRef.current.has(id));
    prevCompletedRef.current = currentCompleted;

    if (newlyCompleted && firstIncompleteStepId) {
      // Ein Schritt wurde gerade erledigt → nächsten offenen expandieren
      setExpandedStep(firstIncompleteStepId);
    } else if (newlyCompleted && !firstIncompleteStepId) {
      // Alle Schritte erledigt → Gratulations-Text zeigen, dann Tutorial ausblenden
      setExpandedStep(null);
      if (context.userId) {
        localStorage.setItem(`tutorialCompleted_${context.userId}`, 'true');
      }
      setTimeout(() => {
        setShowTutorial(false);
        setActiveTab('chat');
      }, 5000);
    } else if (!expandedStep && firstIncompleteStepId) {
      // Initialer Zustand: ersten offenen expandieren
      setExpandedStep(firstIncompleteStepId);
    }
  }, [completedStepIds, firstIncompleteStepId, expandedStep, context.userId]);

  // Auto-Open bei Refresh wenn applicationCount === 0
  useEffect(() => {
    if (!context.agbAccepted || !context.userId) return;

    if (context.applicationCount === 0 && !autoOpenedRef.current) {
      autoOpenedRef.current = true;
      if (!localStorage.getItem(`tutorialCompleted_${context.userId}`)) {
        setOpen(true);
        setActiveTab('tutorial');
        setShowTutorial(true);
      }
    }
  }, [context.agbAccepted, context.userId, context.applicationCount]);

  // Reagiere auf Kontext-Aenderungen mit debounced API-Trigger + Modal-Close-Tracking
  useEffect(() => {
    if (!context.agbAccepted || !context.userId) return;

    if (initialLoadRef.current) {
      initialLoadRef.current = false;
      prevStateRef.current = {
        activeModal: context.activeModal,
        hasJobDescription: context.hasJobDescription,
        matchScore: context.matchScore,
        hasOptimizedCv: context.hasOptimizedCv,
        cvTemplateSettingsSaved: context.cvTemplateSettingsSaved,
        hasMotivationLetter: context.hasMotivationLetter,
        cvDataSetCount: context.cvDataSetCount,
        applicationCount: context.applicationCount,
      };
      return;
    }

    const prev = prevStateRef.current;
    const changed =
      prev.activeModal !== context.activeModal ||
      prev.hasJobDescription !== context.hasJobDescription ||
      prev.matchScore !== context.matchScore ||
      prev.hasOptimizedCv !== context.hasOptimizedCv ||
      prev.cvTemplateSettingsSaved !== context.cvTemplateSettingsSaved ||
      prev.hasMotivationLetter !== context.hasMotivationLetter ||
      prev.cvDataSetCount !== context.cvDataSetCount ||
      prev.applicationCount !== context.applicationCount;

    if (!changed) {
      return;
    }

    // === Fortschritt sofort bei Kontext-Änderung persistieren ===
    if (context.hasJobDescription && !jobDescriptionCompleted) setJobDescriptionCompleted(true);
    if (context.matchScore !== null && !matchScoreCompleted) setMatchScoreCompleted(true);
    if (context.hasOptimizedCv && !cvOptimizedCompleted) setCvOptimizedCompleted(true);
    if (context.cvTemplateSettingsSaved && !cvTemplateSettingsSaved) setCvTemplateSettingsSaved(true);

    // === Modal-Close-Tracking fuer Tutorial ===
    if (prev.activeModal && !context.activeModal) {
      const closedModal = prev.activeModal;

      // Schritt 2: CV-Daten pruefen abgeschlossen, wenn CV-Daten Modal geschlossen wurde
      if (closedModal === 'cvData' && context.cvDataSetCount > 0) {
        setCvDataUploadedAndModalClosed(true);
      }

      // Schritt 4: CV-Daten Modal geschlossen (nur wenn Schritte 1-3 erledigt)
      if (closedModal === 'cvData'
        && context.cvDataSetCount > 0
        && (context.hasJobDescription || jobDescriptionCompleted)
        && (context.matchScore !== null || matchScoreCompleted)
      ) {
        setCvDataModalClosed(true);
      }

      // Schritt 6: CV-Preview Modal geschlossen (nach Optimierung)
      if (closedModal === 'cvPreview' && (context.hasOptimizedCv || cvOptimizedCompleted)) {
        setCvPreviewModalClosedAfterOpt(true);
      }

      // Schritt 7: Motivationsschreiben Modal geschlossen
      if (closedModal === 'motivationLetter') {
        setMotivationModalClosed(true);
      }

      // Schritt 8: Bewerbungsdetails Modal geschlossen
      if (closedModal === 'applicationDetails') {
        setAppDetailsModalClosed(true);
      }
    }

    // Baue Trigger-Reason aus den Aenderungen
    const reasons: string[] = [];
    if (prev.activeModal !== context.activeModal) {
      if (context.activeModal) reasons.push(`Modal opened: ${context.activeModal}`);
      else if (prev.activeModal) reasons.push(`Modal closed: ${prev.activeModal}`);
    }
    if (!prev.hasJobDescription && context.hasJobDescription) reasons.push('Job description loaded');
    if (prev.matchScore === null && context.matchScore !== null) reasons.push('Match score calculated');
    if (!prev.hasOptimizedCv && context.hasOptimizedCv) reasons.push('CV optimized');
    if (!prev.cvTemplateSettingsSaved && context.cvTemplateSettingsSaved) reasons.push('Template settings saved');
    if (!prev.hasMotivationLetter && context.hasMotivationLetter) reasons.push('Motivation letter created');
    if (prev.cvDataSetCount !== context.cvDataSetCount) reasons.push('CV data sets changed');
    if (prev.applicationCount !== context.applicationCount) reasons.push('Application count changed');

    prevStateRef.current = {
      activeModal: context.activeModal,
      hasJobDescription: context.hasJobDescription,
      matchScore: context.matchScore,
      hasOptimizedCv: context.hasOptimizedCv,
      cvTemplateSettingsSaved: context.cvTemplateSettingsSaved,
      hasMotivationLetter: context.hasMotivationLetter,
      cvDataSetCount: context.cvDataSetCount,
      applicationCount: context.applicationCount,
    };

    const triggerReason = reasons.join(', ') || 'Context changed';

    if (!openRef.current) {
      setShowPulse(true);
      setTimeout(() => setShowPulse(false), 3000);
    }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [context.activeModal, context.hasJobDescription, context.matchScore,
      context.hasOptimizedCv, context.hasMotivationLetter,
      context.cvDataSetCount, context.applicationCount]);

  // Auto-scroll zum Ende bei neuen Nachrichten
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  // Fokus auf Input wenn Drawer geöffnet wird
  useEffect(() => {
    if (open && inputRef.current && activeTab === 'chat') {
      setTimeout(() => inputRef.current?.focus(), 300);
    }
  }, [open, activeTab]);

  const getWelcomeMessage = (): string => {
    switch (context.language) {
      case 'en':
        return 'Hi! I\'m your assistant. How can I help you?';
      case 'fr':
        return 'Bonjour ! Je suis votre assistant. Comment puis-je vous aider ?';
      case 'it':
        return 'Ciao! Sono il tuo assistente. Come posso aiutarti?';
      default:
        return 'Hallo! Ich bin dein Assistent. Wie kann ich dir helfen?';
    }
  };

  const getPlaceholder = (): string => {
    switch (context.language) {
      case 'en':
        return 'Ask a question...';
      case 'fr':
        return 'Posez une question...';
      case 'it':
        return 'Fai una domanda...';
      default:
        return 'Stelle eine Frage...';
    }
  };

  const getTitle = (): string => {
    switch (context.language) {
      case 'en':
        return 'Help Assistant';
      case 'fr':
        return 'Assistant d\'aide';
      case 'it':
        return 'Assistente';
      default:
        return 'Hilfe-Assistent';
    }
  };

  const getTutorialCheckboxLabel = (): string => {
    switch (context.language) {
      case 'en': return 'Show tutorial';
      case 'fr': return 'Afficher le tutoriel';
      case 'it': return 'Mostra tutorial';
      default: return 'Tutorial anzeigen';
    }
  };

  const getTutorialTitle = (): string => {
    switch (context.language) {
      case 'en':
        return 'Tutorial';
      case 'fr':
        return 'Tutoriel';
      case 'it':
        return 'Tutorial';
      default:
        return 'Tutorial';
    }
  };

  const getProgressLabel = (): string => {
    switch (context.language) {
      case 'en':
        return `${completedSteps} of ${totalSteps} completed`;
      case 'fr':
        return `${completedSteps} sur ${totalSteps} terminé(s)`;
      case 'it':
        return `${completedSteps} di ${totalSteps} completato/i`;
      default:
        return `${completedSteps} von ${totalSteps} erledigt`;
    }
  };

  const getAllDoneMessage = (): string => {
    switch (context.language) {
      case 'en':
        return '🎉 Great job! All steps are completed. Your application is ready!';
      case 'fr':
        return '🎉 Bravo ! Toutes les étapes sont terminées. Votre candidature est prête !';
      case 'it':
        return '🎉 Ottimo lavoro! Tutti i passaggi sono completati. La tua candidatura è pronta!';
      default:
        return '🎉 Super! Alle Schritte sind erledigt. Deine Bewerbung ist bereit!';
    }
  };

  const getOptionalLabel = (): string => {
    switch (context.language) {
      case 'en': return 'optional';
      case 'fr': return 'optionnel';
      case 'it': return 'opzionale';
      default: return 'optional';
    }
  };

  const getRateLimitMessage = (retryAfter?: number): string => {
    const waitInfo = retryAfter && retryAfter > 0
      ? ` (${Math.max(1, Math.ceil(retryAfter / 60))} min)`
      : '';
    switch (context.language) {
      case 'en':
        return `Too many requests right now. Please wait a moment and try again${waitInfo}.`;
      case 'fr':
        return `Trop de requêtes pour le moment. Veuillez patienter un instant puis réessayer${waitInfo}.`;
      case 'it':
        return `Troppe richieste al momento. Attendi un momento e riprova${waitInfo}.`;
      default:
        return `Zu viele Anfragen im Moment. Bitte warte kurz und versuche es erneut${waitInfo}.`;
    }
  };

  const getCvLengthHintMessage = (): string => {
    switch (context.language) {
      case 'en':
        return 'Your CV preview currently has more than 2 pages. I can help you shorten it:\n1. Shorten the short profile and work experience texts.\n2. Move work experience entries to "Additional work experience".\n3. Hide "Additional work experience".';
      case 'fr':
        return 'Votre aperçu CV contient actuellement plus de 2 pages. Je peux vous aider à le raccourcir :\n1. Raccourcir les textes du profil court et des expériences professionnelles.\n2. Déplacer des expériences vers "Expériences professionnelles supplémentaires".\n3. Masquer "Expériences professionnelles supplémentaires".';
      case 'it':
        return 'L’anteprima del CV ha attualmente più di 2 pagine. Posso aiutarti ad accorciarlo:\n1. Accorciare i testi del profilo breve e delle esperienze lavorative.\n2. Spostare alcune esperienze in "Ulteriori esperienze lavorative".\n3. Nascondere "Ulteriori esperienze lavorative".';
      default:
        return 'Deine CV-Vorschau hat aktuell mehr als 2 Seiten. Ich kann dir helfen, sie zu kürzen:\n1. Texte im Kurzprofil und in den Berufserfahrungen kürzen.\n2. Berufserfahrungen in "Weitere Berufserfahrungen" verschieben.\n3. "Weitere Berufserfahrungen" ausblenden.';
    }
  };

  useEffect(() => {
    const pageCount = Number(context.cvPreviewPageCount || 0);
    const isCvPreviewOpen = context.activeModal === 'cvPreview';
    const shouldShowHint = isCvPreviewOpen && pageCount > 2;

    if (!isCvPreviewOpen) {
      cvLengthHintShownRef.current = false;
      return;
    }

    if (!shouldShowHint || cvLengthHintShownRef.current) return;

    cvLengthHintShownRef.current = true;
    const hintMessage: ChatMessage = {
      role: 'assistant',
      content: getCvLengthHintMessage(),
    };
    // Replace existing chat history with the CV length hint.
    setMessages([hintMessage]);

    if (!openRef.current) {
      setShowPulse(true);
      setTimeout(() => setShowPulse(false), 3000);
    }
  }, [context.activeModal, context.cvPreviewPageCount, context.language]);

  const handleSend = async () => {
    const question = input.trim();
    if (!question || loading) return;

    // Wechsle automatisch zum Chat-Tab wenn Nachricht gesendet wird
    if (activeTab !== 'chat') {
      setActiveTab('chat');
    }

    const newUserMessage: ChatMessage = { role: 'user', content: question };
    const updatedMessages = [...messages, newUserMessage];
    setMessages(updatedMessages);
    setInput('');
    setLoading(true);

    try {
      const response = await fetch(`${config.backend.fullApiUrl}/api/assistant`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          question,
          context,
          history: messages, // Bisherigen Verlauf mitsenden
        }),
      });

      if (!response.ok) {
        let apiError = 'Netzwerkfehler';
        let retryAfter: number | undefined;
        try {
          const errorData = await response.json();
          if (typeof errorData?.error === 'string') {
            apiError = errorData.error;
          }
          if (typeof errorData?.retryAfter === 'number') {
            retryAfter = errorData.retryAfter;
          }
        } catch {
          // Fallback: response body ggf. nicht als JSON lesbar
        }

        const httpError: AssistantHttpError = new Error(apiError);
        httpError.status = response.status;
        httpError.retryAfter = retryAfter;
        throw httpError;
      }

      const data = await response.json();
      const assistantMessage: ChatMessage = {
        role: 'assistant',
        content: data.answer || 'Entschuldigung, ich konnte keine Antwort generieren.',
      };
      setMessages([...updatedMessages, assistantMessage]);
    } catch (error) {
      console.error('Fehler beim AI-Assistenten:', error);
      const typedError = error as AssistantHttpError;
      const isRateLimited = typedError?.status === 429;

      const errorMessage: ChatMessage = {
        role: 'assistant',
        content: isRateLimited
          ? getRateLimitMessage(typedError?.retryAfter)
          : context.language === 'en'
          ? 'Sorry, an error occurred. Please try again.'
          : context.language === 'fr'
          ? 'Désolé, une erreur est survenue. Veuillez réessayer.'
          : context.language === 'it'
          ? 'Mi dispiace, si è verificato un errore. Riprova.'
          : 'Entschuldigung, es ist ein Fehler aufgetreten. Bitte versuche es erneut.',
      };
      setMessages([...updatedMessages, errorMessage]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleClearChat = () => {
    setMessages([]);
  };

  const toggleStepExpanded = (stepId: string) => {
    setExpandedStep(prev => {
      const nextExpandedStep = prev === stepId ? null : stepId;

      // Beim Aufklappen weit unten liegende Schritte nach oben scrollen
      if (nextExpandedStep) {
        requestAnimationFrame(() => {
          const container = tutorialScrollRef.current;
          const stepElement = stepRefs.current[stepId];
          if (!container || !stepElement) return;

          const containerRect = container.getBoundingClientRect();
          const stepRect = stepElement.getBoundingClientRect();
          const isStepFarBelow = stepRect.top > containerRect.top + containerRect.height * 0.65;

          if (isStepFarBelow) {
            const targetTop = stepElement.offsetTop - 12;
            container.scrollTo({
              top: Math.max(targetTop, 0),
              behavior: 'smooth',
            });
          }
        });
      }

      return nextExpandedStep;
    });
  };

  // ========= RENDER =========

  const renderBotInstruction = (instruction: string) => {
    const warningConfigByLanguage: Record<string, { regex: RegExp; text: string }> = {
      de: {
        regex: /Dieser Wert sollte mindestens \*\*75%\*\* betragen\./,
        text: 'Dieser Wert sollte mindestens 75% betragen.',
      },
      en: {
        regex: /The score should be at least \*\*75%\*\*\./,
        text: 'The score should be at least 75%.',
      },
      fr: {
        regex: /Le score devrait être d'au moins \*\*75%\*\*\./,
        text: "Le score devrait être d'au moins 75%.",
      },
      it: {
        regex: /Il punteggio dovrebbe essere almeno del \*\*75%\*\*\./,
        text: 'Il punteggio dovrebbe essere almeno del 75%.',
      },
    };

    const warningConfig = warningConfigByLanguage[context.language] || warningConfigByLanguage.de;
    const warningMatch = instruction.match(warningConfig.regex);
    const shouldHighlightWarning = !!warningMatch;
    const warningText = warningMatch?.[0] || '';
    const instructionBeforeWarning = warningMatch ? instruction.slice(0, warningMatch.index) : instruction;
    const instructionAfterWarning = warningMatch ? instruction.slice((warningMatch.index || 0) + warningText.length) : '';

    return (
      <Box
        sx={{
          bgcolor: '#f0f2ff',
          borderRadius: 1.5,
          px: 1.5,
          py: 1,
          border: '1px solid #e0e4ff',
          mb: 1,
          '& p': { margin: 0, lineHeight: 1.5 },
          '& strong': { fontWeight: 600, color: '#4a5568' },
        }}
      >
        {shouldHighlightWarning ? (
          <>
            <ReactMarkdown
              components={{
                p: ({ children }) => (
                  <Typography variant="caption" component="p" sx={{ color: '#555', lineHeight: 1.5, fontSize: '0.75rem' }}>
                    {children}
                  </Typography>
                ),
              }}
            >
              {instructionBeforeWarning}
            </ReactMarkdown>
            <Typography
              variant="caption"
              component="p"
              sx={{ color: '#d32f2f', lineHeight: 1.5, fontSize: '0.75rem', fontWeight: 400, fontStyle: 'italic' }}
            >
              {warningConfig.text}
            </Typography>
            <ReactMarkdown
              components={{
                p: ({ children }) => (
                  <Typography variant="caption" component="p" sx={{ color: '#555', lineHeight: 1.5, fontSize: '0.75rem' }}>
                    {children}
                  </Typography>
                ),
              }}
            >
              {instructionAfterWarning}
            </ReactMarkdown>
          </>
        ) : (
          <ReactMarkdown
            components={{
              p: ({ children }) => (
                <Typography variant="caption" component="p" sx={{ color: '#555', lineHeight: 1.5, fontSize: '0.75rem' }}>
                  {children}
                </Typography>
              ),
            }}
          >
            {instruction}
          </ReactMarkdown>
        )}
      </Box>
    );
  };

  const renderTutorialContent = () => (
    <Box
      ref={tutorialScrollRef}
      sx={{
        flex: 1,
        overflowY: 'auto',
        overflowX: 'hidden',
        minHeight: 0,
        px: 2,
        py: 2,
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
        bgcolor: '#f8f9fa',
      }}
    >
      {/* Fortschrittsbalken */}
      <Box sx={{ mb: 1, flexShrink: 0 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
          <Typography variant="caption" sx={{ fontWeight: 600, color: '#555', fontSize: '0.75rem' }}>
            {getProgressLabel()}
          </Typography>
          <Typography variant="caption" sx={{ fontWeight: 700, color: '#667eea', fontSize: '0.8rem' }}>
            {progressPercent}%
          </Typography>
        </Box>
        <LinearProgress
          variant="determinate"
          value={progressPercent}
          sx={{
            height: 8,
            borderRadius: 4,
            bgcolor: '#e0e0e0',
            '& .MuiLinearProgress-bar': {
              borderRadius: 4,
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              transition: 'transform 0.6s ease',
            },
          }}
        />
      </Box>

      {/* Alle Schritte erledigt */}
      {completedSteps === totalSteps && (
        <Box
          sx={{
            bgcolor: '#f0f2ff',
            borderRadius: 2,
            px: 2,
            py: 1.5,
            mb: 1,
            flexShrink: 0,
            border: '1px solid #e0e4ff',
          }}
        >
          <Typography variant="body2" sx={{ color: '#5a3d8a', fontWeight: 500 }}>
            {getAllDoneMessage()}
          </Typography>
        </Box>
      )}

      {/* Tutorial-Schritte */}
      {tutorialSteps.map((step, index) => {
        const isCompleted = tutorialDisplayCompletionMap[step.id];
        const isExpanded = expandedStep === step.id;

        return (
          <Box
            key={step.id}
            ref={(element) => { stepRefs.current[step.id] = element instanceof HTMLDivElement ? element : null; }}
            sx={{
              bgcolor: 'white',
              borderRadius: 2,
              boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
              overflow: 'hidden',
              flexShrink: 0,
              border: isExpanded ? '1px solid #667eea' : '1px solid transparent',
              transition: 'border-color 0.2s ease',
            }}
          >
            {/* Step Header */}
            <Box
              onClick={() => toggleStepExpanded(step.id)}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                px: 2,
                py: 1.5,
                cursor: 'pointer',
                '&:hover': { bgcolor: '#fafafa' },
                transition: 'background 0.15s ease',
              }}
            >
              {/* Checkbox */}
              {isCompleted ? (
                <CheckCircleIcon sx={{ fontSize: 22, color: '#667eea', flexShrink: 0 }} />
              ) : (
                <RadioButtonUncheckedIcon sx={{ fontSize: 22, color: '#bdbdbd', flexShrink: 0 }} />
              )}

              {/* Title */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, minWidth: 0 }}>
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 600,
                    color: isCompleted ? '#667eea' : '#333',
                    textDecoration: isCompleted ? 'line-through' : 'none',
                    fontSize: '0.85rem',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {`${index + 1}. ${step.getTitle(context.language)}`}
                </Typography>
                {step.optional && (
                  <Typography
                    variant="caption"
                    sx={{
                      color: '#999',
                      fontSize: '0.65rem',
                      fontStyle: 'italic',
                      flexShrink: 0,
                    }}
                  >
                    ({getOptionalLabel()})
                  </Typography>
                )}
              </Box>

              {/* Expand/Collapse */}
              {isExpanded ? (
                <ExpandLessIcon sx={{ fontSize: 20, color: '#999', flexShrink: 0 }} />
              ) : (
                <ExpandMoreIcon sx={{ fontSize: 20, color: '#999', flexShrink: 0 }} />
              )}
            </Box>

            {/* Expanded Content */}
            <Collapse in={isExpanded}>
              <Box sx={{ px: 2, pb: 1.5, pt: 0 }}>
                {/* Bot-Anleitung */}
                {renderBotInstruction(step.getInstruction(context.language, context))}
              </Box>
            </Collapse>
          </Box>
        );
      })}
    </Box>
  );

  const renderChatContent = () => (
    <Box
      sx={{
        flex: 1,
        overflowY: 'auto',
        overflowX: 'hidden',
        minHeight: 0,
        px: 2,
        py: 2,
        display: 'flex',
        flexDirection: 'column',
        gap: 1.5,
        bgcolor: '#f8f9fa',
      }}
    >
      {/* Willkommensnachricht wenn keine Nachrichten */}
      {messages.length === 0 && (
        <Box
          sx={{
            display: 'flex',
            gap: 1.5,
            alignItems: 'flex-start',
          }}
        >
          <Avatar
            sx={{
              width: 32,
              height: 32,
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              flexShrink: 0,
              mt: 0.5,
            }}
          >
            <SmartToyOutlinedIcon sx={{ fontSize: 18 }} />
          </Avatar>
          <Box
            sx={{
              bgcolor: 'white',
              borderRadius: '4px 16px 16px 16px',
              px: 2,
              py: 1.5,
              boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
              maxWidth: '85%',
              '& p': { margin: 0, lineHeight: 1.6 },
              '& p + p': { mt: 1 },
              '& ol': { pl: 2.5, my: 0.5 },
              '& li': { fontSize: '0.875rem', lineHeight: 1.7, mb: 0.5 },
              '& strong': { fontWeight: 600 },
            }}
          >
            <ReactMarkdown
              components={{
                p: ({ children }) => (
                  <Typography variant="body2" component="p" sx={{ color: '#333', lineHeight: 1.6 }}>
                    {children}
                  </Typography>
                ),
              }}
            >
              {getWelcomeMessage()}
            </ReactMarkdown>
          </Box>
        </Box>
      )}

      {/* Chat-Nachrichten */}
      {messages.map((msg, index) => (
        <Box
          key={index}
          sx={{
            display: 'flex',
            gap: 1.5,
            alignItems: 'flex-start',
            flexDirection: msg.role === 'user' ? 'row-reverse' : 'row',
          }}
        >
          {msg.role === 'assistant' && (
            <Avatar
              sx={{
                width: 32,
                height: 32,
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                flexShrink: 0,
                mt: 0.5,
              }}
            >
              <SmartToyOutlinedIcon sx={{ fontSize: 18 }} />
            </Avatar>
          )}
          <Box
            sx={{
              bgcolor: msg.role === 'user' ? '#667eea' : 'white',
              color: msg.role === 'user' ? 'white' : '#333',
              borderRadius: msg.role === 'user' ? '16px 4px 16px 16px' : '4px 16px 16px 16px',
              px: 2,
              py: 1.5,
              boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
              maxWidth: '85%',
              minWidth: 0,
              overflowX: 'hidden',
              wordBreak: 'break-word',
              '& p': { margin: 0, lineHeight: 1.6 },
              '& p + p': { mt: 1 },
              '& ul, & ol': { pl: 2.5, my: 0.5 },
              '& li': { fontSize: '0.875rem', lineHeight: 1.5 },
              '& code': {
                bgcolor: msg.role === 'user' ? 'rgba(255,255,255,0.2)' : '#f0f0f0',
                px: 0.5,
                borderRadius: 0.5,
                fontSize: '0.8rem',
              },
              '& strong': {
                fontWeight: 600,
              },
            }}
          >
            {msg.role === 'assistant' ? (
              <ReactMarkdown
                components={{
                  p: ({ children }) => (
                    <Typography variant="body2" component="p" sx={{ lineHeight: 1.6 }}>
                      {children}
                    </Typography>
                  ),
                }}
              >
                {msg.content}
              </ReactMarkdown>
            ) : (
              <Typography variant="body2" sx={{ lineHeight: 1.6 }}>
                {msg.content}
              </Typography>
            )}
          </Box>
        </Box>
      ))}

      {/* Loading-Indikator */}
      {loading && (
        <Box
          sx={{
            display: 'flex',
            gap: 1.5,
            alignItems: 'flex-start',
          }}
        >
          <Avatar
            sx={{
              width: 32,
              height: 32,
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              flexShrink: 0,
              mt: 0.5,
            }}
          >
            <SmartToyOutlinedIcon sx={{ fontSize: 18 }} />
          </Avatar>
          <Box
            sx={{
              bgcolor: 'white',
              borderRadius: '4px 16px 16px 16px',
              px: 2,
              py: 1.5,
              boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
              display: 'flex',
              alignItems: 'center',
              gap: 1,
            }}
          >
            <CircularProgress size={16} sx={{ color: '#667eea' }} />
            <Typography variant="body2" sx={{ color: '#999', fontStyle: 'italic' }}>
              {context.language === 'en' ? 'Thinking...' :
               context.language === 'fr' ? 'Réflexion...' :
               context.language === 'it' ? 'Sto pensando...' :
               'Denke nach...'}
            </Typography>
          </Box>
        </Box>
      )}

      <div ref={messagesEndRef} />
    </Box>
  );

  return (
    <>
      {/* Floating Action Button */}
      {!open && (
        <Tooltip title={getTitle()} placement="left">
          <Fab
            onClick={() => {
              setOpen(true);
              setIsCollapsed(false);
              setShowPulse(false);
            }}
            sx={{
              position: 'fixed',
              bottom: 24,
              right: 24,
              zIndex: ASSISTANT_Z_INDEX,
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white',
              width: 56,
              height: 56,
              boxShadow: showPulse
                ? '0 0 0 0 rgba(102,126,234,0.7)'
                : '0 4px 16px rgba(102,126,234,0.4)',
              transition: 'all 0.2s ease',
              animation: showPulse ? 'aiAssistantPulse 1.5s ease-in-out infinite' : 'none',
              '@keyframes aiAssistantPulse': {
                '0%': {
                  boxShadow: '0 0 0 0 rgba(102,126,234,0.7)',
                  transform: 'scale(1)',
                },
                '50%': {
                  boxShadow: '0 0 0 16px rgba(102,126,234,0)',
                  transform: 'scale(1.1)',
                },
                '100%': {
                  boxShadow: '0 0 0 0 rgba(102,126,234,0)',
                  transform: 'scale(1)',
                },
              },
              '&:hover': {
                background: 'linear-gradient(135deg, #5a6fd6 0%, #6a4196 100%)',
                transform: 'scale(1.08)',
                boxShadow: '0 6px 20px rgba(102,126,234,0.5)',
              },
            }}
          >
            <SmartToyOutlinedIcon sx={{ fontSize: 28 }} />
          </Fab>
        </Tooltip>
      )}

      {/* Chat Drawer */}
      <Drawer
        anchor="right"
        open={open}
        onClose={() => {
          setOpen(false);
          setIsCollapsed(false);
        }}
        hideBackdrop
        disableScrollLock
        disableEnforceFocus
        sx={{ 
          zIndex: ASSISTANT_Z_INDEX,
          pointerEvents: 'none',
          '& .MuiDrawer-paper': {
            right: 0,
            left: 'auto',
            marginLeft: 'auto',
            zIndex: ASSISTANT_Z_INDEX,
          },
        }}
        PaperProps={{
          onMouseLeave: () => {
            setIsHovered(false);
            // Sonderfall: Maus verlässt den Browser rechts -> Assistant offen lassen.
            const rightEdgeTolerance = 4;
            const pointerLikelyLeftViewportOnRight =
              typeof window !== 'undefined' &&
              window.event instanceof MouseEvent &&
              !window.event.relatedTarget &&
              window.event.clientX >= (window.innerWidth - rightEdgeTolerance);

            setPointerExitedOnRightEdge(pointerLikelyLeftViewportOnRight);
            if (hasModalOverlapRisk && !pointerLikelyLeftViewportOnRight) {
              setIsCollapsed(true);
            }
          },
          onMouseEnter: () => {
            setIsHovered(true);
            setPointerExitedOnRightEdge(false);
            if (isCollapsed) {
              setIsCollapsed(false);
            }
          },
          sx: {
            width: isCollapsed ? COLLAPSED_DRAWER_WIDTH : { xs: '100%', sm: EXPANDED_DRAWER_WIDTH },
            maxWidth: '100vw',
            height: isCollapsed ? '60dvh' : '100dvh',
            top: isCollapsed ? '20dvh' : 0,
            bottom: isCollapsed ? '20dvh' : 'auto',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            position: 'relative',
            right: 0,
            left: 'auto',
            marginLeft: 'auto',
            pointerEvents: 'auto',
            zIndex: ASSISTANT_Z_INDEX,
            boxShadow: '-4px 0 24px rgba(0,0,0,0.12)',
            borderTopLeftRadius: isCollapsed ? 18 : 0,
            borderBottomLeftRadius: isCollapsed ? 18 : 0,
            borderTopRightRadius: 0,
            borderBottomRightRadius: 0,
            transition: `width ${COLLAPSE_TRANSITION_MS}ms cubic-bezier(0.16, 1, 0.3, 1), box-shadow ${COLLAPSE_TRANSITION_MS}ms ease`,
            willChange: 'width',
          },
        }}
      >
          <Box
            sx={{
              position: 'relative',
              flex: 1,
              minHeight: 0,
              overflow: 'hidden',
            }}
          >
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                // Keep collapsed state clickable, but avoid a full translucent overlay.
                background: 'transparent',
                opacity: isCollapsed ? 1 : 0,
                transform: 'translateX(0)',
                pointerEvents: isCollapsed ? 'auto' : 'none',
                transition: `opacity ${COLLAPSE_TRANSITION_MS}ms ease, transform ${COLLAPSE_TRANSITION_MS}ms ease`,
              }}
            >
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  boxShadow: '0 4px 16px rgba(102,126,234,0.35)',
                }}
              >
                <SmartToyOutlinedIcon sx={{ color: 'white', fontSize: 28 }} />
              </Box>
            </Box>
            <Box
              sx={{
                position: 'relative',
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
                opacity: isCollapsed ? 0 : 1,
                transform: 'translateX(0)',
                pointerEvents: isCollapsed ? 'none' : 'auto',
                transition: `opacity ${COLLAPSE_TRANSITION_MS}ms ease, transform ${COLLAPSE_TRANSITION_MS}ms ease`,
              }}
            >
        {/* Header */}
        <Box
          sx={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white',
            px: 2.5,
            py: 1.5,
            flexShrink: 0,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <SmartToyOutlinedIcon sx={{ fontSize: 26 }} />
              <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1.1rem' }}>
                {getTitle()}
              </Typography>
              <IconButton
                size="small"
                onClick={() => setShowTutorialToggle(prev => !prev)}
                sx={{
                  color: 'rgba(255,255,255,0.8)',
                  '&:hover': { color: 'white' },
                  transform: showTutorialToggle ? 'rotate(180deg)' : 'rotate(0deg)',
                  transition: 'transform 0.2s ease',
                  p: 0.25,
                }}
              >
                <KeyboardArrowDownIcon fontSize="small" />
              </IconButton>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              {activeTab === 'chat' && (
                <Tooltip title={context.language === 'en' ? 'Clear chat' : context.language === 'fr' ? 'Effacer le chat' : context.language === 'it' ? 'Cancella chat' : 'Chat leeren'}>
                  <IconButton
                    onClick={handleClearChat}
                    size="small"
                    sx={{ color: 'rgba(255,255,255,0.8)', '&:hover': { color: 'white' } }}
                  >
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
              <IconButton
                onClick={() => setOpen(false)}
                size="small"
                sx={{ color: 'rgba(255,255,255,0.8)', '&:hover': { color: 'white' } }}
              >
                <CloseIcon />
              </IconButton>
            </Box>
          </Box>
          <Collapse in={showTutorialToggle}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={showTutorial}
                  onChange={(e) => {
                    setShowTutorial(e.target.checked);
                    if (e.target.checked) {
                      setActiveTab('tutorial');
                      onTutorialReset?.();
                      localStorage.clear();
                      setCvDataUploadedAndModalClosed(false);
                      setCvDataModalClosed(false);
                      setCvPreviewModalClosedAfterOpt(false);
                      setCvTemplateSettingsSaved(false);
                      setMotivationModalClosed(false);
                      setAppDetailsModalClosed(false);
                      setJobDescriptionCompleted(false);
                      setMatchScoreCompleted(false);
                      setCvOptimizedCompleted(false);
                    }
                  }}
                  size="small"
                  sx={{
                    color: 'rgba(255,255,255,0.7)',
                    '&.Mui-checked': { color: 'white' },
                    py: 0,
                  }}
                />
              }
              label={
                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.9)', fontSize: '0.8rem' }}>
                  {getTutorialCheckboxLabel()}
                </Typography>
              }
              sx={{ ml: 3.5, mt: 0.5, mb: -0.5 }}
            />
          </Collapse>
        </Box>

        {/* Tab-Umschaltung - nur sichtbar wenn Tutorial aktiviert */}
        {showTutorial && (
          <Box
            sx={{
              display: 'flex',
              borderBottom: '1px solid #e0e0e0',
              bgcolor: 'white',
              flexShrink: 0,
            }}
          >
            <Box
              onClick={() => setActiveTab('tutorial')}
              sx={{
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 0.75,
                py: 1.2,
                cursor: 'pointer',
                borderBottom: activeTab === 'tutorial' ? '2px solid #667eea' : '2px solid transparent',
                color: activeTab === 'tutorial' ? '#667eea' : '#999',
                transition: 'all 0.2s ease',
                '&:hover': { color: '#667eea', bgcolor: '#f5f5ff' },
              }}
            >
              <SchoolIcon sx={{ fontSize: 18 }} />
              <Typography variant="body2" sx={{ fontWeight: activeTab === 'tutorial' ? 600 : 400, fontSize: '0.85rem' }}>
                {getTutorialTitle()}
              </Typography>
              {/* Badge mit Fortschritt */}
              <Box
                sx={{
                  bgcolor: '#667eea',
                  color: 'white',
                  borderRadius: '10px',
                  px: 0.8,
                  py: 0.1,
                  fontSize: '0.65rem',
                  fontWeight: 700,
                  lineHeight: 1.4,
                }}
              >
                {completedSteps}/{totalSteps}
              </Box>
            </Box>
            <Box
              onClick={() => setActiveTab('chat')}
              sx={{
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 0.75,
                py: 1.2,
                cursor: 'pointer',
                borderBottom: activeTab === 'chat' ? '2px solid #667eea' : '2px solid transparent',
                color: activeTab === 'chat' ? '#667eea' : '#999',
                transition: 'all 0.2s ease',
                '&:hover': { color: '#667eea', bgcolor: '#f5f5ff' },
              }}
            >
              <ChatIcon sx={{ fontSize: 18 }} />
              <Typography variant="body2" sx={{ fontWeight: activeTab === 'chat' ? 600 : 400, fontSize: '0.85rem' }}>
                Chat
              </Typography>
              {messages.length > 0 && (
                <Box
                  sx={{
                    bgcolor: '#667eea',
                    color: 'white',
                    borderRadius: '10px',
                    px: 0.8,
                    py: 0.1,
                    fontSize: '0.65rem',
                    fontWeight: 700,
                    lineHeight: 1.4,
                  }}
                >
                  {messages.length}
                </Box>
              )}
            </Box>
          </Box>
        )}

        {/* Content */}
        {showTutorial && activeTab === 'tutorial' ? renderTutorialContent() : renderChatContent()}

        {/* Input-Bereich (immer sichtbar) */}
        <Box
          sx={{
            p: 2,
            borderTop: '1px solid #e0e0e0',
            bgcolor: 'white',
            flexShrink: 0,
            overflowX: 'hidden',
          }}
        >
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-end', minWidth: 0 }}>
            <TextField
              inputRef={inputRef}
              fullWidth
              multiline
              maxRows={4}
              size="small"
              placeholder={getPlaceholder()}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              disabled={loading}
              sx={{
                flex: 1,
                minWidth: 0,
                '& .MuiOutlinedInput-root': {
                  borderRadius: 3,
                  fontSize: '0.9rem',
                  '&.Mui-focused fieldset': {
                    borderColor: '#667eea',
                  },
                },
              }}
            />
            <IconButton
              onClick={handleSend}
              disabled={!input.trim() || loading}
              sx={{
                bgcolor: input.trim() && !loading ? '#667eea' : '#e0e0e0',
                color: 'white',
                width: 40,
                height: 40,
                borderRadius: 2,
                flexShrink: 0,
                transition: 'all 0.2s ease',
                '&:hover': {
                  bgcolor: '#5a6fd6',
                },
                '&.Mui-disabled': {
                  bgcolor: '#e0e0e0',
                  color: '#bdbdbd',
                },
              }}
            >
              <SendIcon sx={{ fontSize: 20 }} />
            </IconButton>
          </Box>
        </Box>
            </Box>
          </Box>
      </Drawer>
    </>
  );
}
